import { useState, useEffect } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import type { Transaction } from '../types';
import { useOverrideTransaction } from '../hooks/useTransactions';
import { formatTxnDate } from '../utils/dates';
import { Button, Modal, Money, Select, TextArea } from './ui';
import { TXN_OVERRIDE_OPTIONS } from '../lib/enums';

interface Props {
  txn: Transaction | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function OverrideTransactionModal({ txn, onClose, onSuccess }: Props) {
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [success, setSuccess] = useState(false);
  const mutation = useOverrideTransaction();

  useEffect(() => {
    if (txn) {
      setStatus(txn.property_status);
      setReason('');
      setSuccess(false);
    }
  }, [txn]);

  const handleSubmit = () => {
    if (!txn || !reason.trim()) return;
    mutation.mutate(
      { transactionId: txn.id, override: { property_status: status, override_reason: reason } },
      {
        onSuccess: () => {
          setSuccess(true);
          onSuccess?.();
          setTimeout(() => {
            onClose();
            setSuccess(false);
          }, 1200);
        },
      },
    );
  };

  return (
    <Modal
      open={!!txn}
      onClose={() => { onClose(); setSuccess(false); }}
      title="Override Classification"
      footer={
        success ? undefined : (
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} loading={mutation.isPending} disabled={!reason.trim()}>
              Apply Override
            </Button>
          </>
        )
      }
    >
      {txn && !success && (
        <div className="space-y-4">
          <div className="rounded-lg bg-surface-well p-3">
            <p className="text-sm font-medium text-ink">{txn.description}</p>
            <p className="mt-1 text-xs text-ink-faint">
              <Money value={Math.abs(txn.amount)} /> — {formatTxnDate(txn.date)}
            </p>
          </div>
          <Select
            label="New Property Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={TXN_OVERRIDE_OPTIONS}
          />
          <TextArea
            label="Reason for Override (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this classification is being changed..."
          />
          {mutation.isError && (
            <div className="flex items-center gap-2 rounded-lg border border-danger bg-danger-soft p-3 text-sm text-danger">
              <XCircle size={16} className="shrink-0" />
              {(mutation.error as Error)?.message || 'Failed to apply override'}
            </div>
          )}
        </div>
      )}
      {success && (
        <div className="flex flex-col items-center justify-center space-y-3 py-6">
          <div className="rounded-full bg-green-100 p-3">
            <CheckCircle size={24} className="text-green-700" />
          </div>
          <p className="t-card text-green-900">Override Applied</p>
          <p className="text-sm text-ink-faint">The classification has been updated.</p>
        </div>
      )}
    </Modal>
  );
}
